import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { ComponentSync, LL_H_C, LL_V_L, ModuleFE_Thunderstorm, TS_PropRenderer, TS_Tabs } from '@nu-art/thunderstorm-frontend/index';
import { logTemplate_allowedParamTypes, logTemplate_allowedSubTypes } from '@app/advisor-content-shared';
import './Editor_LogTemplate.scss';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { Component_EditorHeader, ICONS, viewMode_Edit, viewMode_View } from '@app/styles';
import { DefaultEditor_InputText } from '@app/styles/frontend/ui-manager/ui-components';
import { __stringify } from '@nu-art/ts-common';
import { SearchableItem_LogTemplate } from '../searchable-items.js';
import { extractParamKeysFromLogTemplate, logTemplateParamBuilder, translateLogTemplateToNodes } from './utils.js';
import { Editor_LogTemplate_Sections } from './Editor_LogTemplate_Sections.js';
import { Editor_SmartDocParams } from '../../../_enum/smart-doc-parameter/Editor_SmartDocParams/Editor_SmartDocParams.js';
export class Editor_LogTemplate extends ComponentSync {
    // ######################## Lifecycle ########################
    deriveStateFromProps(nextProps, state) {
        state.editable = nextProps.editable;
        this.setEditableProps(state.editable);
        state.editMode = nextProps.editMode ?? (!state.editable.item._id ? true : (this.state?.editMode ?? false));
        return state;
    }
    // ######################## Logic ########################
    setEditableProps(editable) {
        editable.setPreSaveAction(template => {
            const paramKeys = extractParamKeysFromLogTemplate(template);
            template.params = logTemplateParamBuilder(paramKeys, template.params ?? []);
        });
        editable.setOnChanged(async () => this.forceUpdate());
    }
    getViewMode = () => this.state.editMode ? viewMode_Edit : viewMode_View;
    getItemActions = () => {
        return [{
                label: 'Edit Log Template',
                action: async () => this.setState({ editMode: true }),
            }, {
                label: 'Delete Log Template',
                action: async () => {
                    await this.state.editable.delete();
                    this.props.deselect?.();
                }
            }, {
                label: 'Export JSON',
                action: async () => {
                    const item = this.state.editable.item;
                    ModuleFE_Thunderstorm.downloadFile({
                        fileName: `Log Template - ${item.title}`,
                        mimeType: 'application/json',
                        content: __stringify(item, true)
                    });
                }
            }];
    };
    getEditModeActions = () => {
        //Actions for existing item
        if (this.state.editable.item._id)
            return [{
                    label: 'Done',
                    action: async () => this.setState({ editMode: false }),
                }];
        //Actions for new item
        return [{
                label: 'Cancel',
                action: async () => {
                    return this.props.deselect();
                },
            }, {
                label: 'Save',
                action: async () => {
                    await this.state.editable.save();
                },
            }];
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { id: 'editor__log-template', children: [!this.props.hideHeader && this.renderHeader(), this.renderBody()] });
    }
    renderHeader = () => {
        return _jsx(Component_EditorHeader, { editable: this.state.editable, searchItem: SearchableItem_LogTemplate, editMode: this.state.editMode, itemActions: this.getItemActions(), editModeActions: this.getEditModeActions() });
    };
    renderBody = () => {
        const tabs = [
            {
                uid: 'general',
                title: 'General',
                content: this.render_Tab_General,
            },
            {
                uid: 'params',
                title: this.render_TabHeader_Params,
                content: this.render_Tab_Params,
            }
        ];
        return _jsx(TS_Tabs, { tabs: tabs, className: 'editor__body' });
    };
    // ######################## Render - General Tab ########################
    render_Tab_General = () => {
        return _jsxs(LL_H_C, { className: 'editor__tab__general', children: [_jsxs(LL_V_L, { className: 'editor__column', children: [_jsxs(LL_H_C, { className: 'editor__row', children: [this.renderEditor_Label(), this.renderEditor_Title()] }), this.renderEditor_Sections()] }), this.render_Preview()] });
    };
    // ######################## Render - General Editors ########################
    renderEditor_Label = () => {
        const editable = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Label', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__value', children: editable.item.label }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: editable, prop: 'label' }) }
                ] }) });
    };
    renderEditor_Title = () => {
        const editable = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Title', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__value', children: editable.item.title }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: editable, prop: 'title' }) }
                ] }) });
    };
    render_Preview = () => {
        const template = this.state.editable.item;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Preview', className: 'editor__log-template__preview', children: _jsx("p", { children: translateLogTemplateToNodes(template) }) });
    };
    renderEditor_Sections = () => {
        const sections = this.state.editable.editProp('sections', []);
        const label = () => _jsxs(_Fragment, { children: ["Sections", this.state.editMode && _jsx(ICONS.advisor_avn_add.component, { onClick: () => sections.updateArrayAt('') })] });
        return _jsx(TS_PropRenderer.Vertical, { label: label(), className: 'editor__log-template__sections', children: _jsx(Editor_LogTemplate_Sections, { editable: sections, editMode: this.state.editMode }) });
    };
    // ######################## Render - Params Tab ########################
    render_Tab_Params = () => {
        const params = this.state.editable.editProp('params', []);
        return _jsx(Editor_SmartDocParams, { editable: params, editMode: this.state.editMode, allowedParamTypes: logTemplate_allowedParamTypes, allowedParamSubTypes: logTemplate_allowedSubTypes });
    };
    render_TabHeader_Params = () => {
        const paramErrors = this.state.editable.hasError('params');
        return _jsxs(_Fragment, { children: ["Params ", paramErrors && _jsx(ICONS.attention.component, {})] });
    };
}
//# sourceMappingURL=Editor_LogTemplate.js.map